import {
    ClientAttributesService,
    DataLayerService,
    DateService,
    DebugControlsService,
    DeviceIdService,
    DocumentVisibilityTracker,
    EventAdapterService,
    HashStore,
    HttpService,
    IndexedDBService,
    LockManager,
    LockManagerToken,
    PageInfoService,
    PrefixedCookieStorageToken,
    PulseBroadcastChannel,
    PulseEnvironment,
    Quote,
    RouteHelperService,
    StateService,
    StorageSelectorToken,
    TrafficSourceService,
    UTMTrackerService,
    UnPrefixedCookieStorageToken,
} from '@pulse/browser-shared';
import {
    CryptoService,
    ProviderBuilder,
    PulseRootConfigurationToken,
    PulseStorage,
    SettingsConfigurations,
    StorageSelector,
} from '@pulse/common';
import {
    CookieSyncService,
    ExperimentDebugService,
    ExperimentsService,
    IsTestService,
    PulseSettings,
    PulseSettingsFactory,
    SegmentsResolverService,
    SettingsFetchHelper,
    SettingsHelper,
    SettingsObserver,
    UserRoleService,
} from '../services';
import { CacheService } from '../services/cache.service';
import { QueueService } from '../services/queue.service';
import { ResponseTrackerService } from '../services/response-tracker.service';
import { UsageTrackerService } from '../services/usage-tracker.service';
import { SettingsCacheBroadcastChannelToken, SettingsCacheBroadcastMessage } from './tokens';

export function settingsProviders(builder: ProviderBuilder) {
    return (
        builder
            .useFactory(SettingsConfigurations, (config) => config, [PulseRootConfigurationToken])
            .useFactory(
                ExperimentsService,
                (
                    getStorage: StorageSelector,
                    cookieStorageToken: PulseStorage,
                    config: SettingsConfigurations,
                    dateService: DateService,
                    responseTrackerService: ResponseTrackerService,
                    cacheService: CacheService,
                    usageTrackerService: UsageTrackerService,
                    eventAdapterService: EventAdapterService,
                ) =>
                    new ExperimentsService(
                        getStorage(config.cacheStorageType),
                        cookieStorageToken,
                        dateService,
                        responseTrackerService,
                        cacheService,
                        usageTrackerService,
                        eventAdapterService,
                        config,
                    ),
                [
                    StorageSelectorToken,
                    PrefixedCookieStorageToken,
                    SettingsConfigurations,
                    DateService,
                    ResponseTrackerService,
                    CacheService,
                    UsageTrackerService,
                    EventAdapterService,
                ],
            )
            .useFactory(
                SegmentsResolverService,
                (
                    getStorage: StorageSelector,
                    config: SettingsConfigurations,
                    responseTrackerService: ResponseTrackerService,
                    deviceIdService: DeviceIdService,
                    dateService: DateService,
                ) =>
                    new SegmentsResolverService(
                        responseTrackerService,
                        getStorage(config.cacheStorageType),
                        deviceIdService,
                        dateService,
                    ),
                [
                    StorageSelectorToken,
                    SettingsConfigurations,
                    ResponseTrackerService,
                    DeviceIdService,
                    DateService,
                ],
            )
            .useFactory(
                SettingsCacheBroadcastChannelToken,
                (
                    getStorage: StorageSelector,
                    config: SettingsConfigurations,
                    env: PulseEnvironment,
                ) =>
                    new PulseBroadcastChannel(
                        'pulse-settings-cache',
                        getStorage(config.cacheStorageType),
                        env,
                    ),
                [StorageSelectorToken, SettingsConfigurations, PulseEnvironment],
            )
            .useClass(SettingsHelper, SettingsHelper, [CryptoService])
            .useClass(SettingsFetchHelper, SettingsFetchHelper, [
                ResponseTrackerService,
                DeviceIdService,
                ExperimentsService,
                ExperimentDebugService,
                TrafficSourceService,
                SettingsConfigurations,
                HttpService,
            ])
            .useFactory(
                SettingsObserver,
                (
                    config: SettingsConfigurations,
                    settingsHelper: SettingsHelper,
                    settingsFetchHelper: SettingsFetchHelper,
                    cacheService: CacheService,
                    usageTrackerService: UsageTrackerService,
                    experimentDebugService: ExperimentDebugService,
                    hashStore: HashStore,
                    lockManager: LockManager,
                    queueService: QueueService,
                    userRoleService: UserRoleService,
                    isTestService: IsTestService,
                    stateService: StateService,
                    clientAttributesService: ClientAttributesService,
                    settingsCacheChannel: PulseBroadcastChannel<SettingsCacheBroadcastMessage>,
                    visibilityService: DocumentVisibilityTracker,
                ) =>
                    new SettingsObserver(
                        settingsHelper,
                        settingsFetchHelper,
                        cacheService,
                        config,
                        usageTrackerService,
                        experimentDebugService,
                        hashStore,
                        lockManager,
                        queueService,
                        userRoleService,
                        isTestService,
                        stateService,
                        clientAttributesService,
                        settingsCacheChannel,
                        visibilityService,
                    ),
                [
                    SettingsConfigurations,
                    SettingsHelper,
                    SettingsFetchHelper,
                    CacheService,
                    UsageTrackerService,
                    ExperimentDebugService,
                    HashStore,
                    LockManagerToken,
                    QueueService,
                    UserRoleService,
                    IsTestService,
                    StateService,
                    ClientAttributesService,
                    SettingsCacheBroadcastChannelToken,
                    DocumentVisibilityTracker,
                ],
            )
            .useFactory(
                PulseSettings,
                (
                    settingsObserver: SettingsObserver,
                    config: SettingsConfigurations,
                    experimentsService: ExperimentsService,
                    segmentsResolverService: SegmentsResolverService,
                ) =>
                    new PulseSettings(
                        settingsObserver,
                        config,
                        experimentsService,
                        segmentsResolverService,
                    ),
                [
                    SettingsObserver,
                    SettingsConfigurations,
                    ExperimentsService,
                    SegmentsResolverService,
                ],
            )
            .useClass(QueueService, QueueService, [LockManagerToken])
            .useFactory(
                CacheService,
                (
                    config: SettingsConfigurations,
                    indexedDBService: IndexedDBService,
                    dateService: DateService,
                    responseTrackerService: ResponseTrackerService,
                    quote: Quote,
                    broadcastChannel: PulseBroadcastChannel<SettingsCacheBroadcastMessage>,
                ) =>
                    new CacheService(
                        indexedDBService,
                        dateService,
                        responseTrackerService,
                        quote,
                        config,
                        broadcastChannel,
                    ),
                [
                    SettingsConfigurations,
                    IndexedDBService,
                    DateService,
                    ResponseTrackerService,
                    Quote,
                    SettingsCacheBroadcastChannelToken,
                ],
            )
            .useClass(UsageTrackerService, UsageTrackerService, [])
            .useClass(ResponseTrackerService, ResponseTrackerService, [DataLayerService])
            .useClass(PulseSettingsFactory, PulseSettingsFactory, [PulseSettings, StateService])
            //
            // Settings/Experimentation
            ////////////////////////////
            .useFactory(
                ExperimentDebugService,
                (
                    getStorage: StorageSelector,
                    config: SettingsConfigurations,
                    prefixedCookieStorageToken: PulseStorage,
                    pageInfoService: PageInfoService,
                    routeHalper: RouteHelperService,
                    debugControlService: DebugControlsService,
                ) =>
                    new ExperimentDebugService(
                        getStorage(config.cacheStorageType),
                        prefixedCookieStorageToken,
                        pageInfoService,
                        config,
                        routeHalper,
                        debugControlService,
                    ),
                [
                    StorageSelectorToken,
                    SettingsConfigurations,
                    PrefixedCookieStorageToken,
                    PageInfoService,
                    RouteHelperService,
                    DebugControlsService,
                ],
            )
            .useFactory(
                UserRoleService,
                (config: SettingsConfigurations, getStorage: StorageSelector) =>
                    new UserRoleService(getStorage(config.cacheStorageType)),
                [SettingsConfigurations, StorageSelectorToken],
            )
            .useFactory(
                IsTestService,
                (config: SettingsConfigurations, getStorage: StorageSelector) =>
                    new IsTestService(getStorage(config.cacheStorageType)),
                [SettingsConfigurations, StorageSelectorToken],
            )
            //
            // Cookie Sync
            ////////////////////////////
            .useFactory(
                CookieSyncService,
                (
                    getStorage: StorageSelector,
                    cookieStorageToken: PulseStorage,
                    config: SettingsConfigurations,
                    deviceIdService: DeviceIdService,
                    experimentsService: ExperimentsService,
                    utmService: UTMTrackerService,
                    trafficSourceService: TrafficSourceService,
                    userRoleService: UserRoleService,
                    settingsHelper: SettingsHelper,
                    stateService: StateService,
                    isTestService: IsTestService,
                    experimentDebugService: ExperimentDebugService,
                ) =>
                    new CookieSyncService(
                        getStorage(config.cacheStorageType), // Persistent Storage
                        cookieStorageToken,
                        config,
                        deviceIdService,
                        experimentsService,
                        utmService,
                        trafficSourceService,
                        userRoleService,
                        settingsHelper,
                        stateService,
                        isTestService,
                        experimentDebugService,
                    ),
                [
                    StorageSelectorToken,
                    UnPrefixedCookieStorageToken,
                    SettingsConfigurations,
                    DeviceIdService,
                    ExperimentsService,
                    UTMTrackerService,
                    TrafficSourceService,
                    UserRoleService,
                    SettingsHelper,
                    StateService,
                    IsTestService,
                    ExperimentDebugService,
                ],
            )
    );
}
